import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Loader2, Mic, Hash } from "lucide-react";
import PrisonerBadge from "@/components/PrisonerBadge";

// Trust pill — colour follows the canonical state.
function TrustPill({ value }) {
    const v = (value || "").toString();
    const cls = v.startsWith("[UNKNOWN")
        ? "border-stamp-red text-stamp-red"
        : v === "Yes" || v === "Provisional" || v === "Structural"
            ? "border-stamp-green text-stamp-green"
            : "border-ink/40 text-ink-soft";
    return (
        <span className={`inline-block border px-2 py-0.5 font-heading uppercase text-[10px] tracking-[0.18em] ${cls}`}>
            {v}
        </span>
    );
}

function ActivePill({ value }) {
    const v = (value || "").toString();
    const tone = v.startsWith("Yes")
        ? "border-stamp-green text-stamp-green"
        : v.startsWith("No")
            ? "border-ink/40 text-ink-soft"
            : "border-ink/30 text-ink";
    return (
        <span className={`inline-block border px-2 py-0.5 font-heading uppercase text-[10px] tracking-[0.18em] ${tone}`}>
            {v}
        </span>
    );
}

function StatusPill({ value }) {
    const tone = {
        "Permanent": "bg-ink text-paper-light",
        "Probationary": "border border-stamp-red text-stamp-red",
        "Active": "border border-ink/40 text-ink",
        "Seasonal": "border border-dashed border-ink/40 text-ink-soft",
        "Variable": "border border-dashed border-stamp-red text-stamp-red",
    }[value] || "border border-ink/30 text-ink";
    return (
        <span className={`inline-block px-2 py-0.5 font-heading uppercase text-[10px] tracking-[0.18em] ${tone}`}>
            {value}
        </span>
    );
}

function RosterRow({ p, expanded, onToggle }) {
    return (
        <>
            <tr
                className="border-b border-ink/15 hover:bg-paper-light/40 cursor-pointer align-top"
                onClick={onToggle}
                data-testid={`roster-row-${p.key}`}
            >
                <td className="py-3 pr-3 font-heading text-stamp-red text-lg tracking-[0.05em] whitespace-nowrap">
                    <div className="flex items-center gap-2.5">
                        <span className="border border-ink/40 sf-glow overflow-hidden inline-block">
                            <PrisonerBadge agent={p} size={40} title={`No. ${p.number} · ${p.name}`} />
                        </span>
                        <span className="inline-flex items-center">
                            <Hash className="w-3 h-3 -mt-0.5 mr-1" />{p.number}
                        </span>
                    </div>
                </td>
                <td className="py-3 pr-3">
                    <div className="font-heading uppercase text-sm text-ink tracking-[0.05em]">{p.name}</div>
                    <div className="label-typed mt-0.5">{p.alias}</div>
                </td>
                <td className="py-3 pr-3"><StatusPill value={p.status} /></td>
                <td className="py-3 pr-3"><TrustPill value={p.trusted} /></td>
                <td className="py-3 pr-3"><ActivePill value={p.active} /></td>
                <td className="py-3 pr-3 font-body text-xs text-ink whitespace-nowrap">{p.last_used}</td>
            </tr>
            {expanded && (
                <tr className="bg-paper-light/30 border-b border-ink/10" data-testid={`roster-row-${p.key}-detail`}>
                    <td colSpan={6} className="px-3 py-4">
                        <div className="font-body text-sm text-ink leading-relaxed max-w-3xl">
                            {p.role}
                        </div>
                        {p.note && (
                            <div className="mt-3 pt-3 border-t border-dashed border-ink/15 label-typed">
                                Note · {p.note}
                            </div>
                        )}
                        {p.quirk && (
                            <div className="mt-2 pt-2 border-t border-dashed border-ink/15 label-typed">
                                Quirk on file · {p.quirk}
                            </div>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
}

export default function Roster() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [openKey, setOpenKey] = useState(null);

    useEffect(() => {
        api.get("/roster")
            .then((r) => setData(r.data))
            .catch((e) => setErr(e.message || "Failed"));
    }, []);

    if (err) return <Shell><div className="text-stamp-red label-typed">// {err}</div></Shell>;
    if (!data) return <Shell><div className="flex items-center gap-2 text-ink-soft label-typed"><Loader2 className="w-4 h-4 animate-spin" /> Reading the roster...</div></Shell>;

    const roster = data.roster || [];

    return (
        <Shell>
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-typed tw-prefix">Prisoner numbering · canonical</div>
                    <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1">
                        Roster<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                        Twelve numbers on the wall. Status, trust, activity, last used.
                        Each transaction between two agents carries both numbers (Canon Rule 8).
                        Click a row for the long form.
                    </p>
                </div>
                <div className="pencil text-lg transform rotate-2">
                    "Each handler rotates. The file stays." — No. 2
                </div>
            </div>

            <div className="paper-card tape-corner p-5 mb-10 overflow-x-auto" data-testid="roster-table-card">
                <table className="w-full" data-testid="roster-table">
                    <thead>
                        <tr className="border-b-2 border-ink/30 text-left">
                            <th className="py-2 pr-3 label-typed">No.</th>
                            <th className="py-2 pr-3 label-typed">Agent</th>
                            <th className="py-2 pr-3 label-typed">Status</th>
                            <th className="py-2 pr-3 label-typed">Trusted?</th>
                            <th className="py-2 pr-3 label-typed">Active?</th>
                            <th className="py-2 pr-3 label-typed">Last Used</th>
                        </tr>
                    </thead>
                    <tbody>
                        {roster.map((p) => (
                            <RosterRow
                                key={p.key}
                                p={p}
                                expanded={openKey === p.key}
                                onToggle={() => setOpenKey(openKey === p.key ? null : p.key)}
                            />
                        ))}
                    </tbody>
                </table>
                <div className="mt-4 label-typed">
                    {roster.length} agents on file ·
                    {" "}{roster.filter((r) => r.trusted === "[UNKNOWN — seek]" || r.last_used === "[UNKNOWN — seek]").length} field(s) flagged unknown — seek.
                </div>
            </div>

            <div className="paper-card tape-corner p-5 tilt-l" data-testid="voice-cast">
                <div className="flex items-center gap-2 label-typed tw-prefix mb-3">
                    <Mic className="w-3 h-3" />
                    Voice cast — rotates between Radio drops
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {data.voice_cast.map((v) => (
                        <div
                            key={v.id}
                            className={`border border-ink/25 px-3 py-2 font-body text-xs flex items-center justify-between ${v.rotation === "special" ? "border-dashed bg-paper-dark/30" : "bg-paper-light/50"}`}
                        >
                            <span className="text-ink">{v.label}</span>
                            <span className="label-typed text-[9px]">{v.rotation}</span>
                        </div>
                    ))}
                </div>
            </div>
        </Shell>
    );
}
